import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../api";

/**
 * RULES — the after-hours reply's threshold, and what it lets through.
 *
 * The weights used to be edited here too; they are Inbound ▸ Scoring now, and
 * the left column points there.
 *
 * The panel is the reason this tab is shaped the way it is. Its two surfaces —
 * a histogram of real inbound and five recent leads re-scored — take the config
 * as it stands on screen, unsaved, so that dragging a weight shows what it
 * does before committing to it. That is the same contract the follow-up rule
 * builder's live count has, and for the same reason: having to save a rule to
 * find out it matches everything is the failure it was built to fix.
 *
 * WHAT THE PANEL IS NOT is a model. Every bar is a count of leads that actually
 * arrived, scored by the real scorer — the mockup's annotation says "the
 * histogram is real inbound, not a model" and it is worth keeping true.
 *
 * THE CHIPS DO NOT SUM TO THE SCORE, and that is not a bug to hide. A chip is
 * what a group contributed (points × weight ÷ 10); the score is a percentage of
 * the best possible lead under these weights. The panel says so in one line,
 * because a reader who tries the addition and fails will otherwise conclude the
 * number is wrong.
 */

/*
 * The cohorts the preview can score. `won` is the strongest and is not a
 * pipeline stage — it is status='client', the only cohort where the answer is
 * known rather than pending.
 */
const COHORTS = [
  { key: "recent", label: "Most recent", doc: "The newest leads — what would happen next" },
  { key: "call_scheduled", label: "Call scheduled", doc: "Leads that got as far as a booked call" },
  { key: "in_progress", label: "In progress", doc: "Leads you have met and not yet sent anything to" },
  { key: "proposal_sent", label: "Pricing sent", doc: "Leads you sent pricing to" },
  { key: "agreement", label: "Agreement", doc: "Leads that reached the agreement stage" },
  { key: "won", label: "Won", doc: "Leads that became clients — the only cohort where the answer is known" },
];

/** Debounce, so dragging a slider is one request at the end and not thirty. */
function useDebounced(value, ms) {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setSettled(value), ms);
    return () => clearTimeout(timer);
  }, [value, ms]);
  return settled;
}

export default function RulesTab({ id, config, patch }) {
  return (
    <div className="crm-auto-rules">
      <div className="crm-auto-rules__left">
        {/*
          THE WEIGHTS MOVED to Inbound ▸ Scoring (src/scoring.js), because the
          score is not this automation's alone: the board card, the record, the
          Leads column and Slack's priority read the same one. What stays here is
          this automation's own decision about the score — the threshold beside.
        */}
        <Link to="/inbound/scoring" className="crm-score-link">
          <span className="crm-score-link__text">
            Scores come from Inbound ▸ Scoring <span>· the one scorer, shared with the board and Slack</span>
          </span>
          <span className="crm-score-link__go">edit weights →</span>
        </Link>
      </div>

      <div className="crm-auto-rules__right">
        <ThresholdPanel
          id={id}
          config={config}
          onThreshold={(threshold) => patch("scoring", { threshold })}
        />
      </div>
    </div>
  );
}

/**
 * THE THRESHOLD PANEL. Histogram, then five real leads, both re-scored live.
 */
function ThresholdPanel({ id, config, onThreshold }) {
  const [cohort, setCohort] = useState("recent");
  const settled = useDebounced(JSON.stringify(config), 400);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(null);
  const latest = useRef(0);

  useEffect(() => {
    const token = ++latest.current;
    setLoading(true);
    api(`/automations/${id}/preview`, {
      method: "POST",
      body: JSON.stringify({ config: JSON.parse(settled), cohort }),
    })
      .then((result) => {
        // A slow earlier request must not overwrite a faster later one.
        if (token !== latest.current) return;
        setData(result);
        setFailed(null);
      })
      .catch((err) => {
        if (token === latest.current) setFailed(err.message);
      })
      .finally(() => {
        if (token === latest.current) setLoading(false);
      });
  }, [id, settled, cohort]);

  const threshold = config.scoring.threshold;
  const histogram = data?.histogram;
  const tallest = useMemo(
    () => Math.max(1, ...(histogram?.buckets || []).map((bucket) => bucket.count)),
    [histogram]
  );

  return (
    <>
      <section className={`crm-card crm-auto-threshold${loading ? " is-loading" : ""}`}>
        <header className="crm-auto-threshold__head">
          <h2>Threshold</h2>
          <span className="crm-hint">reply when score ≥</span>
        </header>

        <div className="crm-auto-threshold__row">
          <input
            className="crm-auto-threshold__num crm-num"
            type="number"
            min="0"
            max="100"
            value={threshold}
            onChange={(event) => onThreshold(Math.max(0, Math.min(100, Number(event.target.value))))}
            aria-label="Threshold, 0 to 100"
          />
          <span className="crm-hint">out of 100. Drag or type.</span>
        </div>

        <input
          className="crm-auto-threshold__slider"
          type="range"
          min="0"
          max="100"
          value={threshold}
          onChange={(event) => onThreshold(Number(event.target.value))}
          aria-label="Threshold slider"
        />
        <div className="crm-auto-threshold__scale">
          <span>0</span>
          <span>50</span>
          <span>100</span>
        </div>

        {failed && <p className="crm-hint crm-hint--warn">{failed}</p>}

        {histogram && (
          <>
            <p className="crm-label crm-auto-histlabel">
              Inbound, last {histogram.days} days · {histogram.total} {histogram.total === 1 ? "lead" : "leads"}
            </p>

            {histogram.total === 0 ? (
              /*
                Not "0%". "No inbound yet" and "none of your inbound would pass"
                are different statements, and only one of them is true here.
              */
              <p className="crm-hint">
                No leads have arrived in this window yet, so there is nothing to draw. The threshold still applies.
              </p>
            ) : (
              <>
                <div className="crm-auto-hist" role="img" aria-label={histogramSummary(histogram, threshold)}>
                  {histogram.buckets.map((bucket) => {
                    const passing = bucket.to >= threshold;
                    return (
                      <div
                        key={bucket.from}
                        className={`crm-auto-hist__bar${passing ? " is-passing" : ""}`}
                        style={{ height: `${Math.round((100 * bucket.count) / tallest)}%` }}
                        title={`${bucket.from}–${bucket.to}: ${bucket.count}`}
                      />
                    );
                  })}
                  {/* The threshold, drawn where it actually falls. */}
                  <div className="crm-auto-hist__line" style={{ left: `${threshold}%` }} aria-hidden="true" />
                </div>
                <div className="crm-auto-hist__scale">
                  <span>0</span>
                  <span>50</span>
                  <span>100</span>
                </div>

                <p className="crm-auto-threshold__verdict">
                  At <b>{threshold}</b>, this would have auto-replied to <b>{histogram.passing_share}%</b> of inbound (
                  {histogram.passing} of {histogram.total}).
                  {histogram.blocked > 0 && (
                    <>
                      {" "}
                      <span className="crm-hint">
                        {histogram.blocked} {histogram.blocked === 1 ? "was" : "were"} blocked outright by a keyword,
                        whatever their score.
                      </span>
                    </>
                  )}
                </p>
              </>
            )}
          </>
        )}
      </section>

      <section className="crm-card crm-auto-preview">
        <header className="crm-auto-threshold__head">
          <h2>Preview</h2>
          <span className="crm-hint">re-scored live</span>
        </header>

        {/*
          WHICH LEADS, and it is the difference between a guess and a
          calibration. "Most recent" answers "what happens next"; the stage
          cohorts answer "what would have happened to the leads that turned
          into something" — and if a company that reached pricing scores 34,
          the threshold is in the wrong place.
        */}
        <div className="crm-auto-cohorts">
          {COHORTS.map((entry) => (
            <button
              key={entry.key}
              type="button"
              className={`crm-auto-cohort${cohort === entry.key ? " is-active" : ""}`}
              onClick={() => setCohort(entry.key)}
              title={entry.doc}
            >
              {entry.label}
            </button>
          ))}
        </div>

        {!data?.leads?.length ? (
          <p className="crm-hint">
            {cohort === "recent"
              ? "No leads to score yet."
              : `Nothing in ${COHORTS.find((c) => c.key === cohort).label.toLowerCase()} to score.`}
          </p>
        ) : (
          data.leads.map((lead) => (
            <div className="crm-auto-prevrow" key={lead.contact_id}>
              <div className="crm-auto-prevrow__who">
                <b>{lead.name}</b>
                <span className="crm-hint">
                  {[lead.company, lead.industry, lead.employee_range].filter(Boolean).join(" · ") ||
                    "no firmographics yet"}
                </span>
                {cohort !== "recent" && (
                  <span className="crm-hint">
                    {lead.company_status === "client" ? "became a client" : `reached ${lead.pipeline_stage}`}
                  </span>
                )}
                <div className="crm-auto-chips">
                  {lead.chips.map((chip) => (
                    <span
                      key={chip.group}
                      className={`crm-auto-chip${chip.points < 0 ? " is-negative" : ""}${
                        chip.points === 0 ? " is-zero" : ""
                      }`}
                      title={chip.matched?.length ? `matched ${chip.matched.join(", ")}` : "matched nothing"}
                    >
                      {chip.label} {chip.points > 0 ? `+${chip.points}` : chip.points}
                    </span>
                  ))}
                </div>
                {lead.blocked_reason && <p className="crm-auto-prevrow__blocked">{lead.blocked_reason}</p>}
              </div>
              <div className="crm-auto-prevrow__score">
                <b className="crm-num">{lead.score}</b>
                <span className={`crm-pill crm-pill--${lead.passes ? "passes" : "below"}`}>
                  {lead.passes ? "passes" : "below"}
                </span>
              </div>
            </div>
          ))
        )}

        {/*
          The one line that stops the chips reading as broken arithmetic. A
          reader who adds them up and gets a different number will otherwise
          decide the score is wrong rather than that it is a percentage.
        */}
        <p className="crm-hint crm-auto-preview__foot">
          Chips are what each group contributed, weighted. The score is that total as a share of the best possible
          lead under these weights, which is why the chips do not add up to it — and why the threshold keeps its
          meaning when you move a weight.
        </p>
      </section>
    </>
  );
}

function histogramSummary(histogram, threshold) {
  return `Histogram of ${histogram.total} inbound leads by score. ${histogram.passing} of them are at or above ${threshold}.`;
}
